import _ from 'lodash';
import moment from 'moment';
import { SearchIsState } from '../graphql/generated/graphqlTypes';

interface BaseFacet {
  prefix: string;
  prefixProps: {
    trigger: RegExp;
    markup: string;
    regex: RegExp;
  };
  idsToParams(ids: string[]): Record<string, unknown>;
  alternativePrefixes?: readonly string[];
}

interface Facet extends BaseFacet {
  type: 'topic' | 'user' | 'postState' | 'mention';
}

interface DateFacet extends BaseFacet {
  prefix: string;
  type: 'after' | 'before';
  apiParam: string;
  symbol: string;
}

const ID_PLACEHOLDER = '__id__';

const getDatePrefixProps = ({
  prefix,
  symbol,
  alternativePrefixes = [],
}: {
  prefix: string;
  symbol: string;
  alternativePrefixes?: readonly string[];
}) => ({
  trigger: new RegExp(
    `(\\b(?:${[prefix, ...alternativePrefixes].join('|')})${symbol}(.*))$`,
  ),
  markup: `[${prefix}${symbol}${ID_PLACEHOLDER}]`,
  regex: new RegExp(`\\[${prefix}${symbol}([0-9\\-]*)\\]`),
});

function dateIdsToTimestampFilterParam(key: string, type: DateFacet['type']) {
  return (ids: string[]) => {
    const date = ids[0];

    if (date) {
      const momentDate = moment.utc(date, 'YYYY-MM-DD');
      const isoString = (
        type === 'after' ? momentDate.endOf('day') : momentDate.startOf('day')
      ).toISOString();
      return {
        timestamp: {
          [key]: isoString,
        },
      };
    }

    return {};
  };
}

const dateFacets: DateFacet[] = (
  [
    {
      prefix: 'edited',
      type: 'after',
      apiParam: 'editedAfter',
    },
    {
      prefix: 'archived',
      type: 'after',
      apiParam: 'archivedAfter',
    },
    {
      prefix: 'published',
      type: 'after',
      apiParam: 'publishedAfter',
    },
    {
      prefix: 'verified',
      type: 'after',
      apiParam: 'verifiedAfter',
    },
    {
      prefix: 'edited',
      type: 'before',
      apiParam: 'editedBefore',
    },
    {
      prefix: 'archived',
      type: 'before',
      apiParam: 'archivedBefore',
    },
    {
      prefix: 'published',
      type: 'before',
      apiParam: 'publishedBefore',
    },
    {
      prefix: 'verified',
      type: 'before',
      apiParam: 'verifiedBefore',
    },
  ] as const
).map((facet) => {
  const facetWithSymbol = {
    symbol: facet.type === 'after' ? '>' : '<',
    ...facet,
  };

  return {
    prefixProps: getDatePrefixProps(facetWithSymbol),
    idsToParams: dateIdsToTimestampFilterParam(facet.apiParam, facet.type),
    ...facetWithSymbol,
  };
});

export const postStateData = (
  [
    {
      id: 'published',
      name: 'Published',
    },
    {
      id: 'draft',
      name: 'Draft',
    },
    {
      id: 'archived',
      name: 'Archived',
    },
    {
      id: 'public',
      name: 'Public',
    },
    {
      id: 'verified',
      name: 'Verified',
    },
    {
      id: 'expired',
      name: 'Expired',
    },
  ] as const
).map((postState) => ({
  ...postState,
  display: postState.name,
}));

const getPrefixProps = ({
  prefix,
  alternativePrefixes = [],
}: {
  prefix: string;
  alternativePrefixes?: readonly string[];
}) => ({
  trigger: new RegExp(
    `(\\b(?:${[prefix, ...alternativePrefixes].join('|')}):(.*))$`,
  ),
  markup: `[${prefix}:${ID_PLACEHOLDER}]`,
  regex: new RegExp(`\\[${prefix}:([a-z0-9]+)\\]`),
});

export const facets: Facet[] = (
  [
    {
      prefix: 'in',
      type: 'topic',
      idsToParams: (ids: string[]) => ({
        ancestorIds: ids,
      }),
    },
    {
      prefix: 'maintainer',
      alternativePrefixes: ['maintain', 'maintained'],
      type: 'user',
      idsToParams: (ids: string[]) => ({
        ownerId: ids[0],
      }),
    },
    {
      prefix: 'owner',
      alternativePrefixes: ['own', 'owned'],
      type: 'user',
      idsToParams: (ids: string[]) => ({
        ownerId: ids[0],
      }),
    },
    {
      prefix: 'contributor',
      alternativePrefixes: [
        'contributed',
        'contribute',
        'editor',
        'author',
        'authored',
      ],
      type: 'user',
      idsToParams: (ids: string[]) => ({
        contributorIds: ids,
      }),
    },
    {
      prefix: 'by',
      type: 'user',
      idsToParams: (ids: string[]) => ({
        byIds: ids,
      }),
    },
    {
      prefix: 'is',
      type: 'postState',
      idsToParams: (states: string[]) => ({
        isStates: states.map((state) => {
          switch (state) {
            case 'archived':
              return SearchIsState.Archived;
            case 'expired':
              return SearchIsState.Expired;
            case 'draft':
              return SearchIsState.Draft;
            case 'public':
              return SearchIsState.Public;
            case 'published':
              return SearchIsState.Published;
            case 'verified':
              return SearchIsState.Verified;
            default:
              return '';
          }
        }),
      }),
      prefixProps: {
        trigger: /(\bis:(.*))$/,
        markup: `[is:${ID_PLACEHOLDER}]`,
        regex: new RegExp(
          `\\[is:(${postStateData.map(({ id }) => id).join('|')})\\]`,
        ),
      },
    },
    {
      prefix: 'mention',
      alternativePrefixes: ['mentions'],
      type: 'mention',
      idsToParams: (ids: string[]) => ({
        mentionIds: ids,
      }),
    },
  ] as const
).map((facet) => {
  return {
    prefixProps: getPrefixProps(facet),
    ...facet,
  };
});

const allFacets = [...dateFacets, ...facets];

export type FacetedSearchInput = {
  query: string;
  ancestorIds?: string[];
  ownerId?: string;
  contributorIds?: string[];
  byIds?: string[];
  mentionIds?: string[];
  isStates?: SearchIsState[];
};

export const parseFacetedSearchInput = (input: string): FacetedSearchInput => {
  let params: FacetedSearchInput = { query: input };
  allFacets.forEach(({ idsToParams, prefixProps: { regex } }) => {
    const ids: string[] = [];
    const query = params.query.replace(
      new RegExp(regex.source, 'g'),
      (_match, id) => {
        ids.push(id);
        return '';
      },
    );

    if (ids.length > 0) {
      params = _.merge({ ...params, query }, idsToParams(ids));
    }
  });

  return params;
};

export const facetedSearchInputTriggered = (input: string) =>
  allFacets.some(({ prefixProps: { trigger } }) => input.match(trigger));
